import { Link } from 'react-router-dom';
import { ArrowRight, Flame, Wine, Library, LayoutGrid } from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';

const CustomFeaturesPage = () => {
  const features = [
    {
      icon: Flame,
      title: 'Custom Fireplaces',
      description: 'Stunning fireplace surrounds with natural stone, marble, or tile. Electric and gas options available.',
      image: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/y82vqawa_1000069194.jpg',
      alt: 'Custom fireplace Calgary with natural stone surround and wood beam mantel'
    },
    {
      icon: LayoutGrid,
      title: 'Feature Walls',
      description: 'Floor-to-ceiling accent walls with marble, tile, or custom millwork for dramatic impact.',
      image: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/7qdfn1i1_1000069172.jpg',
      alt: 'Calgary basement feature wall with marble tile and modern linear fireplace'
    },
    {
      icon: Wine,
      title: 'Wet Bars & Wine Rooms',
      description: 'Custom wet bars with cabinetry, sinks, and refrigeration. Climate-controlled wine storage.',
      image: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/eas5t004_1000069166.jpg',
      alt: 'Calgary basement renovation with modern fireplace and premium flooring'
    },
    {
      icon: Library,
      title: 'Built-Ins & Shelving',
      description: 'Custom cabinetry, bookcases, and storage solutions designed for your space.',
      image: 'https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/k340dda3_1000069049.jpg',
      alt: 'Custom bathroom renovation Calgary with built-in storage and premium finishes'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="custom-features-page">
      <Navigation />
      
      {/* Hero Section - Using real fireplace photo */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_built-to-last-4/artifacts/7qdfn1i1_1000069172.jpg"
            alt="Custom marble fireplace feature wall Calgary - basement contractors"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0f172a]/60" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Our Services
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
            Custom Features
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Elevate your home with custom-built features. From stunning fireplaces 
            to sophisticated feature walls and built-in storage.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Custom Work
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a] mb-6">
              Details That Make the Difference
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              The finest homes are defined by their details. Our custom feature work 
              transforms ordinary spaces into extraordinary ones, adding character, 
              functionality, and lasting value to your Calgary home.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid - Real Photos */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Real Projects Completed in Calgary
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              Custom Feature Gallery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white overflow-hidden border border-slate-100 hover:border-slate-200 transition-all"
                data-testid={`custom-feature-${index}`}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/60 to-transparent" />
                  <div className="absolute bottom-4 left-6 flex items-center gap-3">
                    <feature.icon size={24} className="text-[#c5a059]" />
                    <h3 className="font-heading text-xl font-semibold text-white">
                      {feature.title}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
              Our Process
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0f172a]">
              How We Work
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your vision and requirements' },
              { step: '02', title: 'Design', desc: 'Create detailed plans and specifications' },
              { step: '03', title: 'Build', desc: 'Expert craftsmanship and installation' },
              { step: '04', title: 'Reveal', desc: 'Final walkthrough and handover' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <span className="text-5xl font-heading font-bold text-[#c5a059]/20">
                  {item.step}
                </span>
                <h3 className="font-heading text-xl font-semibold text-[#0f172a] mt-2 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Have a Custom Project in Mind?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss your custom feature ideas. We'll help bring your vision to life 
            with quality craftsmanship.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#0f172a] px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white transition-colors"
            data-testid="custom-cta-button"
          >
            Request Free Estimate
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomFeaturesPage;
